import { Capacitor, registerPlugin } from "@capacitor/core";

import type { LibraryContext } from "@/native-shell/library";
import { validWidgetSettings, type CompanionWidgetSnapshot, type WidgetRefresh, type WidgetSettings, type WidgetMode } from "./companion-widget";

import type { PeakWaveWidgetSnapshot } from "@/lib/native/widget-snapshot";

export interface NativeWidgetResult {
  saved: boolean;
  refreshed: boolean;
}

export interface PeakWaveWidgetPlugin {
  saveSnapshot(options: { snapshot: PeakWaveWidgetSnapshot }): Promise<NativeWidgetResult>;
  getCapabilities(): Promise<{ version: number; privateAvailable: boolean }>;
  readSettings(context: LibraryContext): Promise<unknown>;
  setMode(options: WidgetSettings & { mode: WidgetMode }): Promise<unknown>;
  beginRefresh(options: WidgetSettings): Promise<unknown>;
  saveCheckedSnapshot(options: WidgetRefresh & { snapshot: CompanionWidgetSnapshot }): Promise<NativeWidgetResult>;
  clearSnapshot(context?: LibraryContext): Promise<NativeWidgetResult>;
  refreshWidgets(): Promise<{ refreshed: boolean }>;
}

export const PeakWaveWidget = registerPlugin<PeakWaveWidgetPlugin>("PeakWaveWidget");

export function getNativeWidgetPlugin(): PeakWaveWidgetPlugin | null {
  if (typeof window === "undefined") return null;
  if (!Capacitor.isNativePlatform()) return null;
  return PeakWaveWidget;
}

export async function saveNativeWidgetSnapshot(snapshot: PeakWaveWidgetSnapshot): Promise<NativeWidgetResult> {
  const plugin = getNativeWidgetPlugin();
  if (!plugin) return { saved: false, refreshed: false };

  try {
    return await plugin.saveSnapshot({ snapshot });
  } catch {
    return { saved: false, refreshed: false };
  }
}

export async function clearNativeWidgetSnapshot(): Promise<NativeWidgetResult> {
  const plugin = getNativeWidgetPlugin();
  if (!plugin) return { saved: false, refreshed: false };

  try {
    const capabilities = await nativeWidgetCapabilities();
    const context = capabilities === "ready" ? await (await import("@/lib/offline/context")).offlineContext() : undefined;
    const result = await plugin.clearSnapshot(context);
    window.dispatchEvent(new Event(WIDGET_CHANGED));
    return result;
  } catch {
    return { saved: false, refreshed: false };
  }
}

export const WIDGET_CHANGED = "peakwave-widget-changed";
export type WidgetCapability = "ready" | "unavailable" | "update-required";
export async function nativeWidgetCapabilities(): Promise<WidgetCapability> {
  const plugin = getNativeWidgetPlugin();
  if (!plugin) return "unavailable";
  try { const value = await plugin.getCapabilities(); return value.version === 2 && value.privateAvailable === true ? "ready" : "unavailable"; }
  catch { return "update-required"; }
}
export async function readNativeWidgetSettings(context: LibraryContext): Promise<WidgetSettings> {
  const value = await getNativeWidgetPlugin()?.readSettings(context);
  if (!validWidgetSettings(value, context)) throw new Error("The widget choice could not be checked. Reopen settings to retry.");
  return value;
}
export async function setNativeWidgetMode(settings: WidgetSettings, mode: WidgetMode): Promise<WidgetSettings> {
  const { assertOfflineContext } = await import("@/lib/offline/context");
  await assertOfflineContext(settings);
  const value = await getNativeWidgetPlugin()?.setMode({ ...settings, mode });
  if (!validWidgetSettings(value, settings) || value.mode !== mode) throw new Error("The widget choice could not be saved. Reopen settings to retry.");
  window.dispatchEvent(new Event(WIDGET_CHANGED));
  return value;
}
